import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from "../../../context/ThemeContext";
import { useAuth } from "../../../context/AuthContext";
import axios from 'axios';
import FormikForm from "../../form/FormikForm";
import {
  Card,
  Button,
  Spin,
  Alert,
  Descriptions,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Modal,
  Table,
} from 'antd';
import {
  ArrowLeftOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  BuildOutlined,
  CalendarOutlined,
  InfoCircleOutlined,
  EditOutlined,
  FormOutlined,
  FileProtectOutlined,
  EyeOutlined,
  BankOutlined,
  AimOutlined
} from '@ant-design/icons';
import { API_BASE_URL as SHARED_API_BASE_URL } from '../../../utils/config';
import { formatMoney } from '../../../utils/currency';
import { trLower, trIncludes } from '../../../utils/trText';
import OffsiteMap from './OffsiteMap';
import NotlarModal from '../modals/NotlarModal';
import YsOdemeleriPanel from './YsOdemeleriPanel';

const { Text } = Typography;

// sql_queries.SOZLESME_IMZA_DURUMU ile aynı (İmza kolonu eskiden ham kodu her değerde yeşil gösteriyordu)
const IMZA_DURUMU = {
  0: { text: 'İptal (Yapılmadı)', color: 'default' },
  1: { text: 'İmzalandı (Yeni)', color: 'green' },
  2: { text: 'İmzalandı (Rotasyon)', color: 'green' },
  3: { text: 'Feshedildi (İptal)', color: 'red' },
  4: { text: 'Feshedildi (Rotasyon)', color: 'red' },
  5: { text: 'Feshedildi (Söz. Yenileme)', color: 'volcano' },
  6: { text: 'İmzalandı (Söz. Yenileme)', color: 'green' },
  7: { text: 'Belediye Kiracısı', color: 'blue' },
  99: { text: 'İmzalanmadı (Aday)', color: 'gold' },
};

const LocationDetailPage = () => {
  const { maptriksId } = useParams();
  const { darkMode } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [location, setLocation] = useState(null);
  const [ysContracts, setYsContracts] = useState([]);
  const [ysLoading, setYsLoading] = useState(false);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [currentFormId, setCurrentFormId] = useState('341');
  const [modalTitle, setModalTitle] = useState('Lokasyon Detay Formu');
  const [formTableName, setFormTableName] = useState(''); 
  const [formUniqueField, setFormUniqueField] = useState('');
  const [currentUniqueValue, setCurrentUniqueValue] = useState(null);
  const [formExtraData, setFormExtraData] = useState({});
  const [offsiteModalOpen, setOffsiteModalOpen] = useState(false);
  const [offsiteData, setOffsiteData] = useState([]);
  const [offsiteLoading, setOffsiteLoading] = useState(false);
  const [offsiteInfo, setOffsiteInfo] = useState(null);
  const [notlarOpen, setNotlarOpen] = useState(false);
  const API_BASE_URL = SHARED_API_BASE_URL;
  
  useEffect(() => {
    let mounted = true;
    async function fetchDetail() {
      try {
        setLoading(true);
        setError(null);
        if (maptriksId) {
          const res = await axios.post(`${API_BASE_URL}/list/execute-query/lokasyonlar`, {
            params: { id: Number(maptriksId) }
          });
          if (!mounted) return;
          const locationData = res.data?.[0];
          setLocation(locationData);
        }       
      } catch (e) {
        if (!mounted) return;
        setError(e?.response?.data?.detail || e.message);
      } finally {
        if (mounted) setLoading(false);
      }
    }
    
    fetchDetail();
    return () => { mounted = false; };
  }, [maptriksId]);

  useEffect(() => {
    let mounted = true;
    async function fetchYsContracts() {
      if (!maptriksId) return;
      try {
        setYsLoading(true);
        const res = await axios.post(`${API_BASE_URL}/list/execute-query/sozlesmeler`, {
          params: { maptriksId: Number(maptriksId) }
        });
        if (!mounted) return;
        const yerContracts = (Array.isArray(res.data) ? res.data : [])
          .filter(row => row.sozlesme_tipi === 'yer');
        setYsContracts(yerContracts);
      } catch (e) {
        if (mounted) setYsContracts([]);
      } finally {
        if (mounted) setYsLoading(false);
      }
    }

    fetchYsContracts();
    return () => { mounted = false; };
  }, [maptriksId]);

  const openFormModal = (formId, title = 'Form', tableName = 'maptriks_lokasyonlar', uniqueField = 'MaptriksID', uniqueValue = null, extraData = {}, overrideFunction = null) => {
    setModalTitle(title);
    setCurrentFormId(formId);
    setFormTableName(tableName);
    setFormUniqueField(uniqueField);
    setCurrentUniqueValue(uniqueValue || maptriksId);
    setFormExtraData({
      ...extraData,
      ...(overrideFunction && { _override_function: overrideFunction })
    });
    setFormModalOpen(true);
  };
  
  const closeFormModal = () => {
    setFormModalOpen(false);
    setCurrentFormId(null);
    setModalTitle('Form');
    setCurrentUniqueValue(null);
    setFormUniqueField('');
    setFormTableName('');
    setFormExtraData({});
  };

  const handleFormSuccess = (result) => {
    setFormModalOpen(false);
    setCurrentFormId(null);
    setModalTitle('Form');
    setCurrentUniqueValue(null);
    setFormUniqueField('');
    setFormTableName('');
    setFormExtraData({});
    window.location.reload();
  };

  const openOffsiteModal = async () => {
    setOffsiteModalOpen(true);
    if (!maptriksId) return;
    try {
      setOffsiteLoading(true);
      const res = await axios.get(`${API_BASE_URL}/atm/offsite/${maptriksId}`);
      setOffsiteData(Array.isArray(res.data?.data) ? res.data.data : []);
      setOffsiteInfo({
        toplam: res.data?.toplam ?? 0,
        radius_m: res.data?.radius_m ?? 500,
        lokasyon: res.data?.lokasyon || null,
      });
    } catch (e) {
      setOffsiteData([]);
      setOffsiteInfo(null);
    } finally {
      setOffsiteLoading(false);
    }
  };

  const getFieldIcon = (key) => {
    const iconMap = {
      'Lokasyon Adı': <EnvironmentOutlined />,
      'Lokasyon No': <EnvironmentOutlined />,
      'Proje': <InfoCircleOutlined />,
      'Adres': <EnvironmentOutlined />,
      'Koordinat': <EnvironmentOutlined />,
      'Segment': <InfoCircleOutlined />,
      'Genişlik': <BuildOutlined />,
      'Yükseklik': <BuildOutlined />,
      'Derinlik': <BuildOutlined />,
      'Alan (m²)': <BuildOutlined />,
      'Data Referans No': <InfoCircleOutlined />,
      'Yer Sahibi': <BuildOutlined />,
      'Yer Sahibi Tel': <BuildOutlined />,
      'Yer Sahibi Adres': <BuildOutlined />,
      'Kira Tutar': <InfoCircleOutlined />,
      'Ödeme Şekli': <InfoCircleOutlined />,
      'KDV / Stopaj': <InfoCircleOutlined />,
      'Komisyon': <InfoCircleOutlined />,
      'Kiralama Bilgileri': <InfoCircleOutlined />,
      'Kabin Kurulum': <BuildOutlined />,
      'Enerji Bağlantısı': <BuildOutlined />,
      'Sayaç No': <InfoCircleOutlined />,
      'Abone No': <InfoCircleOutlined />,
      'UAVT No': <InfoCircleOutlined />,
      'Açıklama': <InfoCircleOutlined />,
      'Genel Açıklama': <InfoCircleOutlined />,
      'Ekleyen': <InfoCircleOutlined />,
      'İlgili Keşif Personeli': <InfoCircleOutlined />,
      'Keşif Tarihi': <CalendarOutlined />,
    };
    return iconMap[key] || <InfoCircleOutlined />;
  };

  const getStatusColor = (status) => {
    if (status === null || status === undefined) return 'default';
    if (typeof status !== 'string') return 'default';
    const statusLower = trLower(status);
    if (statusLower.includes('aktif') || statusLower.includes('çalışıyor')) return 'success';
    if (statusLower.includes('pasif') || statusLower.includes('arızalı')) return 'error';
    if (statusLower.includes('bakım')) return 'warning';
    return 'default';
  };

  const formatValue = (key, value) => {
    if (value === null || value === '' || value === undefined) {
      return <Text type="secondary">-</Text>;
    }

    if (trIncludes(key, 'tarih') && value) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('tr-TR');
        }
      } catch (e) {
      }
    }

    if (trIncludes(key, 'durum')) {
      const color = getStatusColor(value);
      return <Tag color={color}>{String(value)}</Tag>;
    }

    if (key === 'segment') {
      return <Text>{String(value)}</Text>;
    }

    return <Text>{String(value)}</Text>;
  };

  const fieldGroups = useMemo(() => {
    if (!location) return [];

    const groups = [
      {
        title: 'Temel Bilgiler',
        icon: <EnvironmentOutlined />,
        items: [
          { key: 'Lokasyon Adı', label: 'Lokasyon Adı', value: location['Lokasyon Adı'] },
          { key: 'maptriksID', label: 'Lokasyon No', value: location['maptriksID'] },
          { key: 'Proje', label: 'Proje', value: location['Proje'] },
          { key: 'Adres', label: 'Adres', value: location['Adres'] },
          { key: 'Enlem Boylam', label: 'Koordinat', value: location['Enlem Boylam'] },
          { key: 'Segment', label: 'Segment', value: location['Segment'] },
        ]
      },
      {
        title: 'Kurulum Bilgileri',
        icon: <CalendarOutlined />,
        items: [
          { 
            key: 'Kurulum Tarihi', 
            label: 'Kabin Kurulum', 
            value: location['Kurulum Tarihi'] 
              ? `${new Date(location['Kurulum Tarihi']).toLocaleDateString('tr-TR')} Tarihinde Kuruldu` 
              : 'Yok'
          },
          { 
            key: 'Enerji Verilme Tarihi', 
            label: 'Enerji Bağlantısı', 
            value: location['Enerji Verilme Tarihi'] 
              ? `${new Date(location['Enerji Verilme Tarihi']).toLocaleDateString('tr-TR')} Tarihinde Bağlandı` 
              : 'Yok'
          },
        ]
      },
      {
        title: 'Sayaç Bilgileri',
        icon: <InfoCircleOutlined />,
        items: [
          { 
            key: 'Sayaç No', 
            label: 'Sayaç No', 
            value: (() => {
              const sayaclar = [];
              for (let i = 1; i <= 12; i++) {
                if (location[`sayac_${i}`]) {
                  sayaclar.push(`${i}. ${location[`sayac_${i}`]}`);
                }
              }
              return sayaclar.length > 0 ? sayaclar.join(' | ') : 'Yok';
            })()
          },
          { 
            key: 'Abone No', 
            label: 'Abone No', 
            value: (() => {
              const aboneler = [];
              for (let i = 1; i <= 15; i++) {
                if (location[`abone_no_${i}`]) {
                  aboneler.push(`${i}. ${location[`abone_no_${i}`]}`);
                }
              }
              return aboneler.length > 0 ? aboneler.join(' | ') : 'Yok';
            })()
          },
          { 
            key: 'UAVT No', 
            label: 'UAVT No', 
            value: (() => {
              const uavtler = [];
              for (let i = 1; i <= 14; i++) {
                if (location[`uavt_${i}`]) {
                  uavtler.push(`${i}. ${location[`uavt_${i}`]}`);
                }
              }
              return uavtler.length > 0 ? uavtler.join(' | ') : 'Yok';
            })()
          },
        ]
      },
      {
        title: 'Açıklamalar',
        icon: <InfoCircleOutlined />,
        items: [
          // 26.10 düzeltmesi: lokasyonlar sorgusu alanı 'Açıklama' adıyla döndürüyor (hep '-' görünüyordu)
          { key: 'aciklama', label: 'Açıklama', value: location['Açıklama'] ?? location['aciklama'] },
          { key: 'genelaciklama', label: 'Genel Açıklama', value: location['genelaciklama'] },
          { key: 'ekleyen', label: 'Ekleyen', value: location['ekleyen'] },
        ]
      },

    ];

    return groups.map(group => ({
      ...group,
      items: group.items.map(item => ({
        key: item.key,
        label: item.label,
        children: formatValue(item.key, item.value),
        icon: getFieldIcon(item.label)
      }))
    }));
  }, [location]);

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px' 
      }}>
        <Space direction="vertical" align="center">
          <Spin size="large" tip="Lokasyon bilgileri yükleniyor..." />
        </Space>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate(-1)}
          >
            Geri Dön
          </Button>
          <Alert
            message="Hata Oluştu"
            description={`Lokasyon bilgileri yüklenirken bir hata oluştu: ${error}`}
            type="error"
            showIcon
            action={
              <Button size="small" onClick={() => window.location.reload()}>
                Tekrar Dene
              </Button>
            }
          />
        </Space>
      </div>
    );
  }

  return (
    <div className={`location-detail-page ${darkMode ? 'dark-theme' : ''}`} style={{ 
      padding: 24, 
      minHeight: '70vh'
    }}>
      <Card
        bodyStyle={{ padding: 16 }}
        style={{
          marginBottom: 24,
        }}
        title={
          <span>
            <FormOutlined /> Lokasyon Detay Formu
          </span>
        }
        extra={
          <Space>
            <Button
              icon={<BankOutlined />}
              onClick={openOffsiteModal}
            >
              Offsite ATM'ler
            </Button>
            <Button
              icon={<FileTextOutlined />}
              onClick={() => setNotlarOpen(true)}
            >
              Notlar
            </Button>
            <Button
              icon={<EditOutlined />}
              onClick={() => {
                const extraData = {
                  ekleyen: user?.kullanici_no,
                };
                openFormModal(341, 'Lokasyon Detay Formu', 'maptriks_lokasyonlar', 'MaptriksID', maptriksId, extraData, 'Update');
              }}
            >
              Formu Aç
            </Button>
          </Space>
        }
      >
      </Card>

      {location && (
        <Row gutter={[24, 24]}>
          {fieldGroups.map((group, index) => (
            <Col xs={24} key={index}>
              <Card
                size="small"
                style={{ height: '100%' }}
                title={
                  <Space>
                    {group.icon}
                    <Text strong>{group.title}</Text>
                  </Space>
                }
              >
                <Descriptions
                  bordered
                  column={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1 }}
                  size="small"
                  items={group.items.map(item => ({
                    key: item.key,
                    label: (
                      <Space>
                        {item.icon}
                        <Text strong>{item.label}</Text>
                      </Space>
                    ),
                    children: item.children
                  }))}
                />
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Card
        size="small"
        style={{ marginTop: 24 }}
        title={
          <Space>
            <FileProtectOutlined />
            <Text strong>Yer Sahibi (YS) Sözleşmeleri</Text>
          </Space>
        }
      >
        <Table
          rowKey={(record) => record.ID ?? `${record.No}-${record['Baş. Tar.']}`}
          size="small"
          loading={ysLoading}
          dataSource={ysContracts}
          pagination={false}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'Bu lokasyona bağlı YS sözleşmesi bulunamadı' }}
          columns={[
            { title: 'Sözleşme No', dataIndex: 'No', key: 'no' },
            { title: 'Ünvan', dataIndex: 'Ünvan', key: 'unvan' },
            { title: 'Sözleşme Tipi', dataIndex: 'Söz. Tip', key: 'tip' },
            {
              title: 'Kira',
              key: 'kira',
              align: 'right',
              render: (_, r) => formatMoney(r['Kira'], r['Döviz'] || 'TRY'),
            },
            { title: 'Ödeme', dataIndex: 'Ödeme', key: 'odeme', render: (v) => v || '-' },
            {
              title: 'Kira Başlangıç',
              dataIndex: 'Kir. Baş. Tar.',
              key: 'kiraBas',
              render: (v) => formatValue('Kira Başlangıç tarih', v),
            },
            {
              title: 'Sözleşme Başlangıç',
              dataIndex: 'Baş. Tar.',
              key: 'basTar',
              render: (v) => formatValue('Başlangıç tarih', v),
            },
            {
              title: 'Sözleşme Bitiş',
              dataIndex: 'Bit. Tar.',
              key: 'bitTar',
              render: (v) => formatValue('Bitiş tarih', v),
            },
            {
              title: 'İmza Durumu',
              dataIndex: 'İmza',
              key: 'imza',
              render: (v) => {
                if (v === null || v === undefined || v === '') return <Tag>Belirtilmemiş</Tag>;
                const d = IMZA_DURUMU[Number(v)];
                return <Tag color={d?.color || 'default'}>{d?.text || String(v)}</Tag>;
              },
            },
            {
              title: '',
              key: 'detay',
              width: 60,
              render: (_, record) => record.ID ? (
                <Button
                  type="text"
                  icon={<EyeOutlined />}
                  title="Sözleşme detayına git"
                  onClick={() => navigate(`/yer_sozlesme_detay/${record.ID}`, {
                    state: { record: { ...record, id: record.ID } }
                  })}
                />
              ) : null,
            },
          ]}
        />
      </Card>

      {/* 26.10 (F4 YS): YS kira faturası / ödeme paneli (salt-okur, ortak bileşen) */}
      <YsOdemeleriPanel maptriksId={maptriksId} style={{ marginTop: 16 }} />

      <NotlarModal
        open={notlarOpen}
        onClose={() => setNotlarOpen(false)}
        maptriksId={maptriksId}
        kullaniciNo={user?.kullanici_no}
      />

      <Modal
        title={
          <Space>
            <BankOutlined />
            <span>Offsite ATM'ler</span>
            {offsiteInfo && (
              <Tag color="blue">{offsiteInfo.toplam} ATM · {offsiteInfo.radius_m} m</Tag>
            )}
          </Space>
        }
        open={offsiteModalOpen}
        onCancel={() => setOffsiteModalOpen(false)}
        footer={null}
        width={820}
        destroyOnHidden={true}
      >
        <OffsiteMap location={offsiteInfo?.lokasyon} atms={offsiteData} />

        {/* 26.10 düzeltmesi: NotlarModal bu modalın içindeydi; Offsite kapalıyken hiç render
            edilmediği için 'Notlar' butonu bu sayfada çalışmıyordu (yukarı taşındı). */}
        <Table
          rowKey={(r, i) => `${r.bankId}-${r.latitude}-${r.longitude}-${i}`}
          size="small"
          loading={offsiteLoading}
          dataSource={offsiteData}
          scroll={{ x: 'max-content', y: 420 }}
          pagination={{ pageSize: 20, showTotal: (t) => `Toplam ${t} offsite ATM` }}
          locale={{ emptyText: 'Bu lokasyonun çevresinde offsite ATM bulunamadı' }}
          columns={[
            { title: 'Banka', dataIndex: 'banka', key: 'banka', width: 200, render: (v) => <strong>{v}</strong> },
            { title: 'Adres', dataIndex: 'adres', key: 'adres', width: 260, ellipsis: true, render: (v) => v || '-' },
            { title: 'Mahalle', dataIndex: 'mahalle', key: 'mahalle', width: 160, render: (v) => v || '-' },
            { title: 'İlçe', dataIndex: 'ilce', key: 'ilce', width: 130, render: (v) => v || '-' },
            {
              title: 'Mesafe',
              dataIndex: 'mesafe_m',
              key: 'mesafe',
              width: 100,
              align: 'right',
              defaultSortOrder: 'ascend',
              sorter: (a, b) => a.mesafe_m - b.mesafe_m,
              render: (v) => <Tag color={v <= 150 ? 'green' : v <= 300 ? 'gold' : 'default'}>{v} m</Tag>,
            },
            {
              title: '',
              key: 'harita',
              width: 60,
              render: (_, r) => (
                <Button
                  type="text"
                  icon={<AimOutlined />}
                  title="Google Maps'te göster"
                  href={`https://www.google.com/maps?q=${r.latitude},${r.longitude}`}
                  target="_blank"
                />
              ),
            },
          ]}
        />
      </Modal>

      {formModalOpen && (
        <Modal
          title={modalTitle}
          open={formModalOpen}
          onCancel={closeFormModal}
          footer={null}
          destroyOnHidden={true}
          width={800}
        >
          <div style={{ maxHeight: 600, overflowY: 'auto' }}>
            <FormikForm
              formId={currentFormId}
              uniqueValue={currentUniqueValue}
              uniqueField={formUniqueField}
              tableName={formTableName}
              extraData={formExtraData}
              onSuccess={handleFormSuccess}
              onCancel={closeFormModal}
            />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default LocationDetailPage;
